import { faCopy } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';

import Box from '~/common/components/Box';
import {
  LoaderItem,
  LoaderItemLabel,
} from '~/common/components/Nav/LeftDrawer/components/LoaderItem';
import Stack from '~/common/components/Stacks/Stack';
import Text from '~/common/components/Texts/Text';
import useGetLoanSummary from '~/common/hooks/useGetLoanSummary';
import Tooltip from '~/common/mui/MUITooltip/MUITooltip';
import colors from '~/theme/colors';

const NavLogoLoanSummary = () => {
  const [copiedLoanNumber, setCopiedLoanNumber] = useState(false);
  const [copiedFull, setCopiedFull] = useState(false);

  const sharedIconStyle = {
    width: '0.875rem',
    height: '1rem',
    paddingLeft: '0.25rem',
    paddingBottom: '0.125rem',
    marginRight: '0.25rem',
  };

  const sharedTooltipStyle = {
    backgroundColor: colors.gray[800],
    lineHeight: '0.875rem',
    fontSize: '0.625rem',
    color: colors.white,
    fontFamily: 'Roboto, sans-serif',
    borderRadius: '0.25rem',
  };

  const sharedArrowStyle = {
    color: colors.gray[800],
  };

  const sharedTextStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: '500',
    lineHeight: '1.5rem',
    cursor: 'pointer',
    userSelect: 'none',
  };

  const { data, status, isFetching, error } = useGetLoanSummary();

  return (
    <Stack
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
      }}
      direction="row"
    >
      <Box sx={{ m: 0 }}>
        <img
          src="/images/bolt-logo-fallback.svg"
          className="uwm-logo"
          alt="BOLT powered by UWM"
          style={{
            height: '5.125rem',
            width: '7.564rem',
            padding: '0 1rem 0 1rem',
          }}
        />
      </Box>
      {isFetching && !data && (
        <Stack
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoaderItemLabel text="PLEASE WAIT" />
          <LoaderItem className="loader" />
        </Stack>
      )}
      {error && <Text>Error: Could not retrieve loan Summary Data</Text>}
      {status === 'success' && data && (
        <Box sx={{ ml: 0 }}>
          <CopyableTextWithToolTip
            text={String(data?.loanInfo?.loanNumber)}
            valueToCopy={String(data?.loanInfo?.loanNumber)}
            tooltipOpen={copiedLoanNumber}
            setTooltipOpen={setCopiedLoanNumber}
            textStyle={{
              ...sharedTextStyle,
              fontSize: '1.25rem',
              color: colors.gray[900],
              '& .faCopy-icon': { color: 'transparent' },
              '&:hover .faCopy-icon': { color: colors.gray[900] },
              '&:active .faCopy-icon': { color: colors.uwmOrange[500] },
              '&:hover': {
                color: colors.gray[900],
                backgroundColor: colors.orange[100],
                borderRadius: '0.25rem',
              },
              '&:active': { color: colors.uwmOrange[500] },
              '&:focus': { color: colors.orange[300] },
            }}
            iconStyle={sharedIconStyle}
            tooltipStyle={sharedTooltipStyle}
            arrowStyle={sharedArrowStyle}
          />
          <CopyableTextWithToolTip
            text={`${data?.borrower?.firstName} ${data?.borrower?.lastName}`}
            valueToCopy={`${data?.borrower?.firstName} ${data?.borrower?.lastName}`}
            tooltipOpen={copiedFull}
            setTooltipOpen={setCopiedFull}
            textStyle={{
              ...sharedTextStyle,
              fontSize: '1rem',
              color: colors.gray[500],
              '& .faCopy-icon': { color: 'transparent' },
              '&:hover .faCopy-icon': { color: colors.gray[500] },
              '&:active .faCopy-icon': { color: colors.uwmLogo.pdfGreenBg },
              '&:hover': {
                color: colors.gray[500],
                backgroundColor: colors.green[100],
                borderRadius: '0.25rem',
              },
              '&:active': { color: colors.uwmLogo.pdfGreenBg },
              '&:focus': { color: colors.uwmLogo.limeGreen },
            }}
            iconStyle={sharedIconStyle}
            tooltipStyle={sharedTooltipStyle}
            arrowStyle={sharedArrowStyle}
          />
        </Box>
      )}
    </Stack>
  );
};

const CopyableTextWithToolTip = ({
  text,
  valueToCopy,
  tooltipOpen,
  setTooltipOpen,
  textStyle,
  iconStyle,
  tooltipStyle,
  arrowStyle,
}: {
  text: string;
  valueToCopy: string;
  tooltipOpen: boolean;
  setTooltipOpen: (open: boolean) => void;
  textStyle: object;
  iconStyle: object;
  tooltipStyle: object;
  arrowStyle: object;
}) => (
  <Tooltip
    open={tooltipOpen}
    title={tooltipOpen ? 'Copied!' : null}
    arrow
    slotProps={{
      tooltip: { sx: tooltipStyle },
      arrow: { sx: arrowStyle },
    }}
  >
    <span>
      <Text
        sx={textStyle}
        onClick={() => {
          navigator.clipboard.writeText(valueToCopy).then(() => {
            setTooltipOpen(true);
            setTimeout(() => setTooltipOpen(false), 1200);
          });
        }}
      >
        {text}
        <FontAwesomeIcon
          className="faCopy-icon"
          icon={faCopy}
          style={iconStyle}
        />
      </Text>
    </span>
  </Tooltip>
);

export default NavLogoLoanSummary;
